/**
 * **What a scan opens** (F3): the family page's QR, a group leader's QR, a tag on its way out,
 * the tag printed again.
 */
import { type ReactNode } from 'react';
import { formatCountDay, formatDateTime, formatTime } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { first, hours, spanOf } from './BoothShared.js';
import { useBooth } from './BoothState.js';

/** What a scan opens: the household, the group, the tag on its way out, the tag again. */
export function ScanCards(): ReactNode {
  const {
    today,
    found,
    picked,
    left,
    setLeft,
    reprinted,
    setReprinted,
    setDone,
    setTag,
    setBatch,
    group,
    setGroup,
    groupPick,
    setGroupPick,
    house,
    setHouse,
    housePick,
    setHousePick,
    houseDone,
    setHouseDone,
    busy,
    setBusy,
    load,
    show,
    houseChosen,
    houseUnsigned,
    groupWaiversLeft,
    andNames,
    checkInHouse,
    day,
    workedBy,
    printTag,
    stationName,
  } = useBooth();
  return (
    <>
      {houseDone && !house ? (
        <div className="booth__found" data-testid="booth-household-in">
          <div className="booth__banner booth__banner--ok" role="status">
            <b>
              {houseDone.tags.map((t) => first(t.name).toUpperCase()).join(' AND ')}{' '}
              {houseDone.tags.length === 1 ? 'IS' : 'ARE'} IN
            </b>
            <span>
              {houseDone.tags.length === 1
                ? 'One tag prints'
                : `${String(houseDone.tags.length)} tags print`}{' '}
              at this station.
              {houseDone.recorded.some((x) => x.waiverSignedAt)
                ? ' Each waiver signed today is on their record.'
                : ''}
            </span>
          </div>
          <section className="booth__card booth__card--ok">
            <div className="booth__cardhead">
              WHAT WAS RECORDED
              <span className="booth__cardsub">
                on each person, for {today?.festivalYear ?? ''}
              </span>
            </div>
            {houseDone.recorded.map((x) => (
              <div className="booth__row" key={`w${x.personId}`}>
                <span className="booth__rowa">
                  <b>
                    {x.name} · waiver {today?.festivalYear ?? ''}
                  </b>
                  <span className="booth__rowb">
                    {x.waiverSignedAt
                      ? `signed ${formatDateTime(x.waiverSignedAt)}`
                      : 'no waiver to sign this year'}
                  </span>
                </span>
                <span className="booth__rowc">
                  {x.minor ? 'Under 18 — the parent who signed is on the record.' : ''}
                </span>
                <span className={`booth__chip booth__chip--${x.waiverSignedAt ? 'ok' : 'mute'}`}>
                  {x.waiverSignedAt ? 'SEALED' : 'NONE THIS YEAR'}
                </span>
              </div>
            ))}
            {houseDone.tags.map((t) => (
              <div className="booth__row" key={`c${t.personId}`}>
                <span className="booth__rowa">
                  <b>
                    {first(t.name)} · checked in · {t.area} {hours(t.startsAt, t.endsAt)}
                  </b>
                  <span className="booth__rowb">
                    by {houseDone.by ?? 'the desk'} at {stationName} · {formatTime(houseDone.at)}
                  </span>
                </span>
                <span className="booth__rowc">
                  {houseDone.family
                    ? `For ${houseDone.family.replace(/^The /, 'the ')}’s commitment.`
                    : 'Not for a family.'}
                </span>
                <span className="booth__chip booth__chip--ok">IN</span>
              </div>
            ))}
          </section>
          <div className="booth__bigrow">
            <button
              type="button"
              className="booth__big booth__big--navy"
              onClick={() => {
                setHouseDone(null);
              }}
            >
              Next
              <span className="booth__bigsub">back to the search · Esc</span>
            </button>
          </div>
        </div>
      ) : null}
      {house ? (
        <div className="booth__found" data-testid="booth-household">
          <section className="booth__card booth__card--red">
            <div className="booth__cardhead">
              {house.name.toUpperCase()}
              <span className="booth__cardsub">{house.says}</span>
            </div>
            {house.members.map((m) => {
              const shift = m.today.find((t) => t.state === 'expected') ?? m.today[0];
              const working = m.today.some((t) => t.state === 'expected');
              const on = housePick.has(m.personId);
              const chip: readonly [string, string] = !working
                ? ['NOT TODAY', 'mute']
                : m.minor && m.needsWaiver
                  ? ['PARENT SIGNS', 'warn']
                  : m.needsWaiver
                    ? ['WAIVER TO SIGN', 'warn']
                    : shift?.state === 'in'
                      ? ['IN', 'ok']
                      : ['READY', 'ok'];
              const swapFrom = house.members.find(
                (w) =>
                  w.personId !== m.personId &&
                  w.today.some((t) => t.state === 'expected' && t.signupId),
              );
              return (
                <div
                  className={`booth__row${working && m.needsWaiver ? ' booth__row--warn' : ''}`}
                  key={m.personId}
                >
                  <span className="booth__rowa">
                    <label>
                      <input
                        type="checkbox"
                        aria-label={m.name}
                        checked={on}
                        disabled={!working}
                        onChange={(e) => {
                          const next = new Set(housePick);
                          if (e.target.checked) next.add(m.personId);
                          else next.delete(m.personId);
                          setHousePick(next);
                        }}
                      />{' '}
                      <b>
                        {m.name}
                        {m.minor && typeof m.age === 'number' ? `, ${String(m.age)}` : ''}
                      </b>
                    </label>
                    <span className="booth__rowb">
                      {working && shift
                        ? `${shift.area} · ${hours(shift.startsAt, shift.endsAt)}`
                        : m.otherDays[0]
                          ? `no shift today · ${formatCountDay(m.otherDays[0].day)}, ${m.otherDays[0].area}`
                          : 'no shift'}
                    </span>
                  </span>
                  <span className="booth__rowc">
                    {!working
                      ? m.minor
                        ? ''
                        : 'Here instead of someone working today? Their shift moves to them.'
                      : m.minor && m.needsWaiver
                        ? 'Under 18. A parent signs for them on the guest screen.'
                        : m.needsWaiver
                          ? 'This year’s waiver is not signed yet — they sign on the guest screen.'
                          : 'Nothing missing.'}
                  </span>
                  <span className={`booth__chip booth__chip--${chip[1]}`}>{chip[0]}</span>
                  {working && m.needsWaiver ? (
                    <button
                      type="button"
                      className="booth__act"
                      onClick={() => {
                        show('waiver', m.personId, shift?.shiftId ?? null);
                      }}
                    >
                      Waiver on the guest screen
                    </button>
                  ) : null}
                  {/* As drawn: the swap is offered to a grown-up (Minh), not a child (Linh, 11). */}
                  {!working && swapFrom && !m.minor ? (
                    <button
                      type="button"
                      className="booth__act"
                      onClick={() => {
                        const t = swapFrom.today.find((x) => x.state === 'expected' && x.signupId);
                        if (t?.signupId) {
                          setHouse(null);
                          workedBy(t.signupId, m, t, 'swap');
                        }
                      }}
                    >
                      Working {first(swapFrom.name)}’s
                    </button>
                  ) : null}
                </div>
              );
            })}
          </section>
          <section className="booth__card booth__card--sky">
            <div className="booth__cardhead">
              IS THIS FOR THE FAMILY?
              <span className="booth__cardsub">asked once for the household</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>
                  {house.familyId
                    ? `Yes — ${house.name.replace(/^The /, 'the ')}’s commitment`
                    : 'No family on the record'}
                </b>
              </span>
              <span className="booth__rowc">
                {house.familyId
                  ? 'The day’s guards run here unchanged. Credit follows the check-out.'
                  : 'Checked in without credit; the volunteer team links them after.'}
              </span>
              <span className={`booth__chip booth__chip--${house.familyId ? 'ok' : 'mute'}`}>
                {house.familyId ? 'YES' : 'NO FAMILY'}
              </span>
            </div>
          </section>
          <div className="booth__bigrow">
            <button
              type="button"
              className="booth__big"
              data-testid="booth-big"
              disabled={busy || houseChosen.length === 0 || houseUnsigned > 0}
              onClick={() => {
                checkInHouse(houseChosen.map((m) => m.personId));
              }}
            >
              {houseChosen.length ? `Check in ${andNames(houseChosen)}` : 'Tap who is here'}
              <span className="booth__bigsub">
                {houseUnsigned
                  ? `${String(houseChosen.length - houseUnsigned)} of ${String(houseChosen.length)} waivers sealed · enabled when all are`
                  : 'the tags print together · Enter'}
              </span>
            </button>
            {houseChosen.length > 1 && houseChosen[0] ? (
              <button
                type="button"
                className="booth__side-act"
                disabled={busy || houseChosen[0].needsWaiver || !!houseChosen[0].minor}
                onClick={() => {
                  if (houseChosen[0]) checkInHouse([houseChosen[0].personId]);
                }}
              >
                Only {first(houseChosen[0].name)}
              </button>
            ) : null}
            <button
              type="button"
              className="booth__side-act"
              onClick={() => {
                setHouse(null);
              }}
            >
              Back to search
            </button>
          </div>
        </div>
      ) : null}
      {group ? (
        <div className="booth__found" data-testid="booth-group">
          <section className="booth__card booth__card--red">
            <div className="booth__cardhead">
              {group.name.toUpperCase()}
              <span className="booth__cardsub">{group.says}</span>
            </div>
            {group.members.map((m, i) => {
              const shift = m.today.find((t) => t.state === 'expected') ?? m.today[0];
              const working = m.today.some((t) => t.state === 'expected');
              const can = !m.needsWaiver && working;
              const leads = i === 0 && m.name === group.leader;
              const chip: readonly [string, string] = !shift
                ? ['NOT YET', 'mute']
                : shift.state === 'in'
                  ? ['HERE', 'ok']
                  : m.needsWaiver
                    ? ['WAIVER FIRST', 'warn']
                    : ['READY', 'ok'];
              return (
                <div
                  className={`booth__row${working && m.needsWaiver ? ' booth__row--warn' : ''}`}
                  key={m.personId}
                >
                  <span className="booth__rowa">
                    <label>
                      <input
                        type="checkbox"
                        aria-label={m.name}
                        checked={groupPick.has(m.personId)}
                        disabled={!can}
                        onChange={(e) => {
                          const next = new Set(groupPick);
                          if (e.target.checked) next.add(m.personId);
                          else next.delete(m.personId);
                          setGroupPick(next);
                        }}
                      />{' '}
                      <b>
                        {m.name}
                        {m.minor && typeof m.age === 'number' ? `, ${String(m.age)}` : ''}
                      </b>
                    </label>
                    <span className="booth__rowb">
                      {leads ? 'leader · ' : ''}
                      {shift
                        ? `${shift.area} · ${hours(shift.startsAt, shift.endsAt)}`
                        : 'no shift today'}
                    </span>
                  </span>
                  <span className="booth__rowc">
                    {!working
                      ? ''
                      : m.needsWaiver && m.minor && !leads
                        ? `Signs on the guest screen. ${group.leader} is the adult present — the parish allows a group’s leader.`
                        : m.needsWaiver
                          ? 'This year’s waiver is not signed yet — they sign on the guest screen.'
                          : 'Waiver signed this year.'}
                  </span>
                  <span className={`booth__chip booth__chip--${chip[1]}`}>{chip[0]}</span>
                  {working && m.needsWaiver ? (
                    <button
                      type="button"
                      className="booth__act"
                      onClick={() => {
                        show('waiver', m.personId, shift?.shiftId ?? null, group.code);
                      }}
                    >
                      Waiver on the guest screen
                    </button>
                  ) : null}
                </div>
              );
            })}
            <div className="booth__cardfoot">
              Tags print as one batch with “{group.name}” under the area.
            </div>
          </section>
          <div className="booth__bigrow">
            <button
              type="button"
              className="booth__big"
              data-testid="booth-big"
              disabled={busy || groupPick.size === 0}
              onClick={() => {
                const g = group;
                setBusy(true);
                api<{
                  group: string;
                  tags: {
                    name: string;
                    area: string;
                    startsAt: string;
                    endsAt: string;
                    tag: string;
                  }[];
                  skipped: { name: string; why: string }[];
                  says: string;
                }>('POST', '/volunteers/booth/group/check-in', {
                  code: g.code,
                  personIds: [...groupPick],
                })
                  .then((r) => {
                    setTag(null);
                    setBatch(
                      r.tags.map((t) => ({
                        name: t.name,
                        area: t.area,
                        hours: hours(t.startsAt, t.endsAt),
                        day,
                        code: t.tag,
                        group: r.group,
                      })),
                    );
                    setDone(
                      `${r.says}${r.skipped.length ? ` ${r.skipped.map((x) => `${x.name}: ${x.why}`).join(' · ')}` : ''}`,
                    );
                    setGroup(null);
                    load();
                    setTimeout(() => {
                      printTag(r.tags.map((t) => t.area));
                    }, 50);
                  })
                  .catch((e: unknown) => {
                    setDone(sentence(e));
                  })
                  .finally(() => {
                    setBusy(false);
                  });
              }}
            >
              Check in {groupPick.size}
              <span className="booth__bigsub">
                {groupWaiversLeft
                  ? `${String(groupWaiversLeft)} ${groupWaiversLeft === 1 ? 'waiver' : 'waivers'} left · the batch prints when done`
                  : 'the tags print as one batch · Enter'}
              </span>
            </button>
            <button
              type="button"
              className="booth__side-act"
              onClick={() => {
                setGroup(null);
              }}
            >
              Back to search
            </button>
          </div>
        </div>
      ) : null}
      {left && !picked && !found && !reprinted ? (
        <div className="booth__found" data-testid="booth-left">
          <div className="booth__banner booth__banner--ok" role="status">
            <b>CHECKED OUT · {formatTime(left.outAt)} · CREDIT AWARDED</b>
            <span>
              {left.area ?? 'Their shift'}
              {left.inAt ? `, in ${formatTime(left.inAt)}` : ''}, out {formatTime(left.outAt)} at{' '}
              {stationName}. The check-out is the award of credit.
            </span>
          </div>
          <section className="booth__card booth__card--ok">
            <div className="booth__cardhead">
              AS THEY LEAVE
              <span className="booth__cardsub">the guest screen says thank you</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>What they earned</b>
                <span className="booth__rowb">
                  {left.inAt
                    ? `${spanOf(left.inAt, left.outAt)} · ${left.area ?? 'the shift'}`
                    : (left.area ?? 'the shift')}
                </span>
              </span>
              <span className="booth__rowc">
                A school family’s hours count toward its commitment.
              </span>
              <span className="booth__chip booth__chip--ok">CREDITED</span>
            </div>
          </section>
          <section className="booth__card booth__card--ok">
            <div className="booth__cardhead">
              FOUR WAYS OUT
              <span className="booth__cardsub">all the same check-out</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Their tag’s QR</b>
                <span className="booth__rowb">at any station</span>
              </span>
              <span className="booth__rowc">The fast path.</span>
              <span className="booth__chip booth__chip--ok">THIS ONE</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>By name</b>
                <span className="booth__rowb">at any station</span>
              </span>
              <span className="booth__rowc">Search, then Check out.</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>The chair’s “done”</b>
                <span className="booth__rowb">the chair app’s roster</span>
              </span>
              <span className="booth__rowc">The area chair checks them out from Today.</span>
            </div>
            <div className="booth__row booth__row--warn">
              <span className="booth__rowa">
                <b>Never checked out</b>
                <span className="booth__rowb">nothing closes on its own</span>
              </span>
              <span className="booth__rowc">
                The check-in stays open with no credit. At Close the booth the area chair answers
                for each one.
              </span>
              <span className="booth__chip booth__chip--warn">OPEN</span>
            </div>
          </section>
          <div className="booth__bigrow">
            <button
              type="button"
              className="booth__big booth__big--navy"
              data-testid="booth-big"
              onClick={() => {
                setLeft(null);
              }}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
      {reprinted && !picked && !found ? (
        <div className="booth__found" data-testid="booth-reprinted">
          <section className="booth__card">
            <div className="booth__cardhead">
              {reprinted.name.toUpperCase()}
              <span className="booth__cardsub">
                {reprinted.area}
                {reprinted.inAt ? ` · in ${formatTime(reprinted.inAt)}` : ''}
              </span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>New tag</b>
                <span className="booth__rowb">prints now</span>
              </span>
              <span className="booth__rowc">The lost tag’s QR stops working.</span>
              <span className="booth__chip booth__chip--ok">PRINTING</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Wrong booth for their area?</b>
                <span className="booth__rowb">no such thing</span>
              </span>
              <span className="booth__rowc">
                Any booth checks anyone in; the tag says where to go.
              </span>
            </div>
          </section>
          <div className="booth__bigrow">
            <button
              type="button"
              className="booth__big booth__big--navy"
              data-testid="booth-big"
              onClick={() => {
                setReprinted(null);
              }}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
